import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Mic, StopCircle, RotateCcw, Send, Loader2, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { entryService } from "@/services/entry-service";

const VoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        
        // Stop all audio tracks
        stream.getTracks().forEach(track => track.stop());
        
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setAudioBlob(null);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = window.setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error("Error accessing microphone:", err);
      toast({
        title: "Microphone access denied",
        description: "Please enable microphone access to use this feature",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const handleSubmit = async () => {
    if (!audioBlob) return;
    
    setIsSubmitting(true);
    
    try {
      // Convert audioBlob to a File object
      const audioFile = new File([audioBlob], "recording.webm", {
        type: audioBlob.type
      });
      
      // Upload the audio file to the API
      const response = await entryService.uploadAudio(audioFile);
      
      toast({
        title: "Recording submitted",
        description: "Your recording has been uploaded successfully",
      });
      
      // Navigate to entry view page with the returned entry_id
      navigate(`/entry/${response.entry_id}`);
    } catch (error: any) {
      console.error('Upload error:', error);
      toast({
        title: "Error submitting recording",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-lg">
      <Button 
        variant="ghost" 
        className="mb-4 flex items-center gap-2"
        onClick={() => navigate("/dashboard")}
      >
        <ArrowLeft size={16} />
        <span>Back to Dashboard</span>
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle className="text-center">Voice Recorder</CardTitle>
          <CardDescription className="text-center">
            Record your voice for analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center py-6">
          {isRecording ? (
            <div className="flex flex-col items-center space-y-4">
              <div className="recording-indicator"></div>
              <p className="text-lg font-medium">Recording... {formatTime(recordingTime)}</p>
              <Button 
                onClick={stopRecording}
                variant="outline" 
                className="rounded-full h-16 w-16 flex items-center justify-center border-red-500 text-red-500 hover:bg-red-50"
              >
                <StopCircle size={32} />
              </Button>
            </div>
          ) : audioBlob ? (
            <div className="flex flex-col items-center space-y-4 w-full">
              <p className="text-lg font-medium">Recording complete</p>
              <p className="text-sm text-muted-foreground">Duration: {formatTime(recordingTime)}</p>
              
              <audio controls className="w-full max-w-md">
                <source src={URL.createObjectURL(audioBlob)} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
              
              <div className="flex gap-4 mt-4">
                <Button 
                  variant="outline" 
                  onClick={resetRecording}
                  className="flex items-center gap-2"
                >
                  <RotateCcw size={16} />
                  <span>Reset</span>
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-brand-500 hover:bg-brand-600 flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      <span>Submit</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-6">
              <p className="text-center text-muted-foreground max-w-xs">
                Press the button below to start recording your voice for analysis
              </p>
              <Button 
                onClick={startRecording}
                className="capture-button bg-brand-500 hover:bg-brand-600"
              >
                <Mic size={32} className="text-white" />
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-center pb-6">
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            {!isRecording && !audioBlob ? 
              "Make sure you're in a quiet environment for best results" : 
              isRecording ? 
                "Speak clearly into your microphone" : 
                "Review your recording before submitting"
            }
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default VoiceRecorder;
